import ApiClient from '../apiClient';
import {
  Creator,
  Partner,
  BalanceHistoryTransaction,
  OnlyFansAccount,
  PaginatedResponse,
  PaginationParams
} from './types';

// ============ Request Params ============

export interface GetCreatorsParams extends PaginationParams {
  sort_by?: '-name' | 'name' | 'created_at' | '-created_at';
  id?: string;
  email?: string;
  name?: string;
  search?: string;
  status?: 'active' | 'wait_confirm' | 'wait_moderation' | 'deactivated' | 'wait_first_deposit';
  type?: 'creator' | 'agency';
  created_at__gte?: string;
}

export interface GetPartnersParams extends PaginationParams {
  sort_by?: '-name' | 'name' | 'created_at' | '-created_at';
  id?: string;
  email?: string;
  name?: string;
  search?: string;
  status?: 'active' | 'wait_confirm' | 'wait_moderation' | 'wait_additional_data_for_moderation' | 'deactivated';
  created_at__gte?: string;
}

export interface GetBalanceHistoryParams extends PaginationParams {}

export interface GetCreatorModelsParams extends PaginationParams {
  name?: string;
  status?: 'active' | 'moderation' | 'wait_media' | 'deactivated';
}

// ============ Response Types ============

export type CreatorsResponse = PaginatedResponse<Creator>;
export type PartnersResponse = PaginatedResponse<Partner>;
export type BalanceHistoryResponse = PaginatedResponse<BalanceHistoryTransaction>;
export type CreatorModelsResponse = PaginatedResponse<OnlyFansAccount>;

// ============ Users API ============

export const usersApi = {
  // ============ Creators ============

  /**
   * Получить список криейторов
   */
  getCreators: async (params?: GetCreatorsParams): Promise<CreatorsResponse> => {
    const response = await ApiClient.get('/staff/creators', { params });
    return response.data;
  },

  /**
   * Получить детали криейтора
   */
  getCreator: async (creatorId: string): Promise<Creator> => {
    const response = await ApiClient.get(`/staff/creators/${creatorId}`);
    return response.data;
  },

  /**
   * Обновить креатора
   */
  updateCreator: async (creatorId: string, data: Partial<Creator>): Promise<Creator> => {
    const response = await ApiClient.patch(`/staff/creators/${creatorId}`, data);
    return response.data;
  },

  /**
   * Изменить статус криейтора
   */
  changeCreatorStatus: async (
    creatorId: string,
    status: 'active' | 'wait_confirm' | 'wait_moderation' | 'deactivated' | 'wait_first_deposit'
  ): Promise<Creator> => {
    const response = await ApiClient.patch(`/staff/creators/${creatorId}`, { status });
    return response.data;
  },

  /**
   * Получить баланс криейтора
   */
  getCreatorBalance: async (creatorId: string): Promise<{ balance: string }> => {
    const response = await ApiClient.get(`/staff/creators/${creatorId}/balance`);
    return response.data;
  },

  /**
   * Получить историю баланса криейтора
   */
  getCreatorBalanceHistory: async (
    creatorId: string,
    params?: GetBalanceHistoryParams
  ): Promise<BalanceHistoryResponse> => {
    const response = await ApiClient.get(`/staff/creators/${creatorId}/balance_histrory`, { params });
    return response.data;
  },

  /**
   * Удалить криейтора
   */
  deleteCreator: async (creatorId: string): Promise<void> => {
    await ApiClient.delete(`/staff/creators/${creatorId}`);
  },

  /**
   * Получить модели (аккаунты) криейтора
   */
  getCreatorModels: async (
    creatorId: string,
    params?: GetCreatorModelsParams
  ): Promise<CreatorModelsResponse> => {
    const response = await ApiClient.get(`/staff/creators/${creatorId}/models`, { params });
    return response.data;
  },

  // ============ Partners ============

  /**
   * Получить список партнеров
   */
  getPartners: async (params?: GetPartnersParams): Promise<PartnersResponse> => {
    const response = await ApiClient.get('/staff/partners', { params });
    return response.data;
  },

  /**
   * Получить детали партнера
   */
  getPartner: async (partnerId: string): Promise<Partner> => {
    const response = await ApiClient.get(`/staff/partners/${partnerId}`);
    return response.data;
  },

  /**
   * Обновить партнера
   */
  updatePartner: async (partnerId: string, data: Partial<Partner>): Promise<Partner> => {
    const response = await ApiClient.patch(`/staff/partners/${partnerId}`, data);
    return response.data;
  },

  /**
   * Изменить статус партнера
   */
  changePartnerStatus: async (
    partnerId: string,
    status: 'active' | 'wait_confirm' | 'wait_moderation' | 'deactivated'
  ): Promise<Partner> => {
    const response = await ApiClient.patch(`/staff/partners/${partnerId}`, { status });
    return response.data;
  },

  /**
   * Получить баланс партнера
   */
  getPartnerBalance: async (partnerId: string): Promise<{ balance: string }> => {
    const response = await ApiClient.get(`/staff/partners/${partnerId}/balance`);
    return response.data;
  },

  /**
   * Получить историю баланса партнера
   */
  getPartnerBalanceHistory: async (
    partnerId: string,
    params?: GetBalanceHistoryParams
  ): Promise<BalanceHistoryResponse> => {
    const response = await ApiClient.get(`/staff/partners/${partnerId}/balance_histrory`, { params });
    return response.data;
  },

  /**
   * Удалить партнера
   */
  deletePartner: async (partnerId: string): Promise<void> => {
    await ApiClient.delete(`/staff/partners/${partnerId}`);
  },

  /**
   * Изменить баланс криейтора
   */
  changeCreatorBalance: async (creatorId: string, amount: number): Promise<void> => {
    await ApiClient.post(`/staff/creators/${creatorId}/change_balance`, { amount });
  },

  /**
   * Изменить баланс партнера
   */
  changePartnerBalance: async (partnerId: string, amount: number): Promise<void> => {
    await ApiClient.post(`/staff/partners/${partnerId}/balance/change`, { amount });
  },
};

export default usersApi;
